import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from 'src/app/data.service';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';

export interface LayoutMezo {
  eazon: string;
  label: string;
  tipus: 'text' | 'checkbox' | 'boxed' | 'dropdown' | 'textarea';
  kotelezo?: boolean;
  sugo?: string;
  mintazat?: string;
  maxHossz?: number;
  minHossz?: number;
  format?: string;
  szamitasTipus?: 'nincs' | 'masol' | 'keplet';
  forrasEazon?: string;
  keplet?: string;
  readonly?: boolean;
  dropdownUrl?: string;
  dropdownValueKey?: string;
  dropdownLabelKey?: string;
  dropdownStatikus?: { ertek: string; nev: string }[];
  kockaDb?: number;
  szeles: number; // 1–6 flex arány
}

export interface LayoutSor {
  id: string;
  mezok: LayoutMezo[];
}

export interface LayoutBlokk {
  id: string;
  cim: string;
  sorok: LayoutSor[];
}

export interface LayoutOldal {
  id: string;
  cim: string;
  formId?: string;  // ENYK form identifier (e.g. '2608A', '2608M') — set during ENYK import
  blokkok: LayoutBlokk[];
}

export interface LayoutDef {
  oldalak: LayoutOldal[];
  /** @deprecated – régi formátum, csak betöltéskor kerül migrálásra */
  blokkok?: LayoutBlokk[];
}

interface FajlSablon {
  fajlnev: string;
  azonosito: string;
  verzio: string;
  adozoNev: string;
}

interface PaletteMezo {
  eazon: string;
  label: string;
  tipusKod: string;
}

@Component({
  selector: 'app-admin-nyomtatvany',
  templateUrl: './nyomtatvany.component.html',
  styleUrls: ['./nyomtatvany.component.scss'],
})
export class AdminNyomtatvanyComponent implements OnInit {

  fajlSablonok: FajlSablon[] = [];
  kivalasztott: FajlSablon | null = null;

  paletteMezok: PaletteMezo[] = [];
  layout: LayoutDef = { oldalak: [] };
  aktivOldal: LayoutOldal | null = null;
  szerkesztettOldalId: string | null = null;
  konfigMezo: LayoutMezo | null = null;
  enykElerhetoBetolt = false;
  enykFeldolgozas = false;

  readonly tipusok = [
    { value: 'text',     label: 'Szöveges mező' },
    { value: 'textarea', label: 'Többsoros szöveg' },
    { value: 'checkbox', label: 'Jelölőnégyzet' },
    { value: 'boxed',    label: 'Karakterdobozok' },
    { value: 'dropdown', label: 'Legördülő (HTTP)' },
  ];

  readonly szelesOpciok = [
    { value: 1, label: '1/6 sor' },
    { value: 2, label: '1/3 sor' },
    { value: 3, label: '1/2 sor' },
    { value: 4, label: '2/3 sor' },
    { value: 5, label: '5/6 sor' },
    { value: 6, label: 'Teljes sor' },
  ];

  private idSzamla = 0;

  constructor(private http: HttpClient, private data: DataService) {}

  ngOnInit() {
    this.betolt();
  }

  stripElotagok(s: string): string {
    return s.replace(/^(NAV|APEH)_/i, '');
  }

  betolt() {
    this.http.get<FajlSablon[]>('/api/nyomtatvany/fajlok').subscribe({
      next: r => (this.fajlSablonok = r),
      error: e => this.data.sendToast('Betöltési hiba: ' + (e?.status ? `HTTP ${e.status}` : e?.message), 'error'),
    });
  }

  kivalaszt(s: FajlSablon) {
    this.enykElerhetoBetolt = false;
    this.http.get<{ fajlnev: string; xml: string; labels?: { mezok: { [k: string]: string } } }>(
      `/api/nyomtatvany/fajlok/${s.fajlnev}`
    ).subscribe({
      next: r => {
        this.kivalasztott = s;
        const labels = r.labels?.mezok || {};
        const regex = /<mezo eazon="([^"]+)">([^<]*)<\/mezo>/g;
        const osszesMezo: PaletteMezo[] = [];
        let match: RegExpExecArray | null;
        while ((match = regex.exec(r.xml)) !== null) {
          osszesMezo.push({
            eazon: match[1],
            label: labels[match[1]] || match[1],
            tipusKod: match[1].length > 6 ? match[1][6].toUpperCase() : '',
          });
        }
        this.http.get<LayoutDef>(`/api/nyomtatvany/layout-konfig/${s.fajlnev}`).subscribe({
          next: lResp => {
            // Migráció: régi { blokkok: [] } → { oldalak: [{ cim: 'Főlap', blokkok: [] }] }
            if (lResp?.oldalak?.length) {
              this.layout = lResp;
            } else if ((lResp as any)?.blokkok?.length) {
              this.layout = { oldalak: [{ id: this.ujId(), cim: 'Főlap', blokkok: (lResp as any).blokkok }] };
            } else {
              this.layout = { oldalak: [] };
            }
            this.aktivOldal = this.layout.oldalak[0] ?? null;
            const benLevo = new Set<string>();
            this.layout.oldalak.forEach(o => o.blokkok.forEach(b => b.sorok.forEach(sor => sor.mezok.forEach(m => benLevo.add(m.eazon)))));
            this.paletteMezok = osszesMezo.filter(m => !benLevo.has(m.eazon));
          },
          error: () => {
            this.layout = { oldalak: [] };
            this.aktivOldal = null;
            this.paletteMezok = osszesMezo;
          },
        });
        // Check if an XSD file exists for this form
        const azonosito = s.fajlnev;
        this.http.head(`/api/nyomtatvany/xsd-import/${azonosito}`).subscribe({
          next: () => (this.enykElerhetoBetolt = true),
          error: () => (this.enykElerhetoBetolt = false),
        });
      },
      error: e => this.data.sendToast('Hiba: ' + (e?.status ? `HTTP ${e.status}` : e?.message), 'error'),
    });
  }

  enykImport() {
    if (!this.kivalasztott) return;
    if (!confirm('Az XSD fájlból automatikusan generált layout betöltése felülírja a jelenlegi szerkesztett layoutot. Folytatod?')) return;
    const azonosito = this.kivalasztott.fajlnev;
    this.enykFeldolgozas = true;
    this.http.get<LayoutDef>(`/api/nyomtatvany/xsd-import/${azonosito}`).subscribe({
      next: layout => {
        this.enykFeldolgozas = false;
        this.layout = layout;
        this.aktivOldal = layout.oldalak[0] ?? null;
        // All fields are now in the layout — palette is empty
        this.paletteMezok = [];
        this.data.sendToast('XSD alapú layout betöltve! Ellenőrizd és mentsd el.', 'success');
      },
      error: e => {
        this.enykFeldolgozas = false;
        this.data.sendToast('XSD import hiba: ' + (e?.error?.error || e?.message), 'error');
      },
    });
  }

  vissza() {
    this.kivalasztott = null;
    this.paletteMezok = [];
    this.layout = { oldalak: [] };
    this.aktivOldal = null;
    this.szerkesztettOldalId = null;
    this.konfigMezo = null;
    this.enykElerhetoBetolt = false;
  }

  // ---- Layout mutációk ----

  private ujId(): string {
    return `i${++this.idSzamla}_${Date.now()}`;
  }

  // -- Oldal (fülek) --

  oldalHozzaad() {
    const ujOldal: LayoutOldal = { id: this.ujId(), cim: `${this.layout.oldalak.length + 1}. oldal`, blokkok: [] };
    this.layout.oldalak.push(ujOldal);
    this.aktivOldal = ujOldal;
  }

  oldalTorol(oldal: LayoutOldal) {
    if (!confirm(`"${oldal.cim}" lap törlése? A benne lévő mezők visszakerülnek a palettera.`)) return;
    oldal.blokkok.forEach(b => b.sorok.forEach(sor => sor.mezok.forEach(m => this.visszaPalettera(m))));
    this.layout.oldalak = this.layout.oldalak.filter(o => o.id !== oldal.id);
    if (this.aktivOldal?.id === oldal.id) this.aktivOldal = this.layout.oldalak[0] ?? null;
    if (this.konfigMezo && !this.mezoMegvanLayoutban(this.konfigMezo.eazon)) this.konfigMezo = null;
  }

  oldalFel(i: number) { if (i > 0) { moveItemInArray(this.layout.oldalak, i, i - 1); } }
  oldalJobb(i: number) { if (i < this.layout.oldalak.length - 1) { moveItemInArray(this.layout.oldalak, i, i + 1); } }

  oldalSzerkesztesAktiv(oldal: LayoutOldal): boolean {
    return this.szerkesztettOldalId === oldal.id;
  }

  // -- Blokkok (az aktív oldalon) --

  blokkHozzaad() {
    if (!this.aktivOldal) return;
    this.aktivOldal.blokkok.push({ id: this.ujId(), cim: 'Új blokk', sorok: [] });
  }

  blokkTorol(blokk: LayoutBlokk) {
    if (!this.aktivOldal) return;
    if (!confirm(`"${blokk.cim}" törlés? A benne lévő mezők visszakerülnek a palettera.`)) return;
    blokk.sorok.forEach(sor => sor.mezok.forEach(m => this.visszaPalettera(m)));
    this.aktivOldal.blokkok = this.aktivOldal.blokkok.filter(b => b.id !== blokk.id);
    if (this.konfigMezo && !this.mezoMegvanLayoutban(this.konfigMezo.eazon)) this.konfigMezo = null;
  }

  blokkFel(i: number) { if (this.aktivOldal && i > 0) moveItemInArray(this.aktivOldal.blokkok, i, i - 1); }
  blokkLe(i: number) { if (this.aktivOldal && i < this.aktivOldal.blokkok.length - 1) moveItemInArray(this.aktivOldal.blokkok, i, i + 1); }

  sorHozzaad(blokk: LayoutBlokk) {
    blokk.sorok.push({ id: this.ujId(), mezok: [] });
  }

  sorTorol(blokk: LayoutBlokk, sor: LayoutSor) {
    sor.mezok.forEach(m => this.visszaPalettera(m));
    blokk.sorok = blokk.sorok.filter(s => s.id !== sor.id);
    if (this.konfigMezo && !this.mezoMegvanLayoutban(this.konfigMezo.eazon)) this.konfigMezo = null;
  }

  sorFel(blokk: LayoutBlokk, i: number) { if (i > 0) moveItemInArray(blokk.sorok, i, i - 1); }
  sorLe(blokk: LayoutBlokk, i: number) { if (i < blokk.sorok.length - 1) moveItemInArray(blokk.sorok, i, i + 1); }

  private visszaPalettera(m: LayoutMezo) {
    if (!this.paletteMezok.some(p => p.eazon === m.eazon)) {
      this.paletteMezok.push({ eazon: m.eazon, label: m.label, tipusKod: '' });
    }
  }

  private mezoMegvanLayoutban(eazon: string): boolean {
    return this.layout.oldalak.some(o => o.blokkok.some(b => b.sorok.some(s => s.mezok.some(m => m.eazon === eazon))));
  }

  // ---- CDK Drag & Drop (csak mezők) ----

  getAllFieldDropIds(): string[] {
    const ids: string[] = ['palette-list'];
    // Csak az aktív oldal sorai fogadhatnak mezőt drag közben
    this.aktivOldal?.blokkok.forEach(b => b.sorok.forEach(s => ids.push('row-' + s.id)));
    return ids;
  }

  paletteDropped(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }
    // LayoutMezo érkezik vissza → PaletteMezo-ra konvertálás
    const item = event.previousContainer.data[event.previousIndex] as LayoutMezo;
    const pm: PaletteMezo = { eazon: item.eazon, label: item.label, tipusKod: '' };
    event.previousContainer.data.splice(event.previousIndex, 1);
    this.paletteMezok.splice(event.currentIndex, 0, pm);
    if (this.konfigMezo?.eazon === item.eazon) this.konfigMezo = null;
  }

  sorMezoDropped(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }
    const item = event.previousContainer.data[event.previousIndex];
    if ('tipusKod' in item) {
      // PaletteMezo → LayoutMezo
      const pm = item as PaletteMezo;
      const ujMezo: LayoutMezo = {
        eazon: pm.eazon,
        label: pm.label,
        tipus: pm.tipusKod === 'F' ? 'checkbox' : pm.tipusKod === 'H' ? 'boxed' : 'text',
        kotelezo: false,
        szamitasTipus: 'nincs',
        forrasEazon: '',
        keplet: '',
        szeles: 3,
      };
      event.previousContainer.data.splice(event.previousIndex, 1);
      event.container.data.splice(event.currentIndex, 0, ujMezo);
    } else {
      // LayoutMezo sorok között
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  // ---- Mező konfiguráció ----

  osszesMezoForras(): Array<{ eazon: string; label: string }> {
    const byEazon = new Map<string, string>();
    this.layout.oldalak.forEach(o => o.blokkok.forEach(b => b.sorok.forEach(s => s.mezok.forEach(m => {
      byEazon.set(m.eazon, m.label || m.eazon);
    }))));
    this.paletteMezok.forEach(p => byEazon.set(p.eazon, p.label || p.eazon));
    return Array.from(byEazon.entries())
      .map(([eazon, label]) => ({ eazon, label }))
      .sort((a, b) => a.eazon.localeCompare(b.eazon));
  }

  mezoKonfig(m: LayoutMezo, event: MouseEvent) {
    event.stopPropagation();
    this.konfigMezo = this.konfigMezo?.eazon === m.eazon ? null : m;
  }

  mezoTorol(sor: LayoutSor, m: LayoutMezo) {
    sor.mezok = sor.mezok.filter(x => x.eazon !== m.eazon);
    this.visszaPalettera(m);
    if (this.konfigMezo?.eazon === m.eazon) this.konfigMezo = null;
  }

  // ---- Mentés ----

  ment() {
    if (!this.kivalasztott) return;
    this.http.put(`/api/nyomtatvany/layout-konfig/${this.kivalasztott.fajlnev}`, this.layout).subscribe({
      next: () => this.data.sendToast('Layout elmentve!', 'success'),
      error: e => this.data.sendToast('Hiba: ' + (e?.status ? `HTTP ${e.status}` : e?.message), 'error'),
    });
  }
}
